import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ChevronLeft, Building2, Users, Send, Activity, MessageSquare, AlertTriangle, Bell, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const GRADE_COLOUR = {
  green: 'text-green-700 bg-green-100',
  yellow: 'text-yellow-700 bg-yellow-100',
  red: 'text-red-700 bg-red-100',
};

const StatRow = ({ label, value, suffix = '' }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
    <span className="text-gray-600">{label}</span>
    <span className="font-semibold">{value}{suffix}</span>
  </div>
);

export default function PlatformBuildingProfile() {
  const { hoaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentComms, setRecentComms] = useState([]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/platform-admin/buildings/${hoaId}/profile`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setProfile(await r.json());
    setLoading(false);
  }, [hoaId]);

  const fetchRecentComms = useCallback(async () => {
    const r = await fetch(`${API}/api/platform-admin/buildings/${hoaId}/communications?limit=10`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setRecentComms(d.communications || []);
    }
  }, [hoaId]);

  useEffect(() => { fetchProfile(); fetchRecentComms(); }, [fetchProfile, fetchRecentComms]);

  if (loading || !profile) {
    return <div className="p-8 text-center" data-testid="profile-loading">Loading…</div>;
  }

  const { building, user_groups, counts, health } = profile;

  const renderUserTable = (rows) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Login</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">No users in this group.</TableCell></TableRow>
          ) : rows.map((u) => (
            <TableRow key={u.user_id}>
              <TableCell className="font-medium">{u.name || '—'}</TableCell>
              <TableCell className="text-sm">{u.email}</TableCell>
              <TableCell>{u.unit_number || '—'}</TableCell>
              <TableCell>{u.phone || '—'}</TableCell>
              <TableCell><Badge variant="outline" className="capitalize">{u.status}</Badge></TableCell>
              <TableCell className="text-xs text-gray-500">{(u.last_login || '').slice(0, 10) || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="building-profile-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Building2 className="w-7 h-7 text-indigo-600" /> {building.name}
            </h1>
            <p className="text-gray-600 text-sm">
              {[building.building_type, building.city, building.country].filter(Boolean).join(' · ')}
            </p>
          </div>
          {user?.role === 'platform_owner' && (
            <Button
              onClick={() => navigate(`/platform/communications?hoas=${hoaId}`)}
              data-testid="message-this-building"
            >
              <Send className="w-4 h-4 mr-1" /> Message Building
            </Button>
          )}
        </div>

        {/* Top cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Total Members</div>
            <div className="text-2xl font-bold" data-testid="count-total-members">{counts.total_members}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Open Tickets</div>
            <div className="text-2xl font-bold">{counts.open_tickets}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Notices</div>
            <div className="text-2xl font-bold">{counts.notices}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Votes</div>
            <div className="text-2xl font-bold">{counts.votes}</div>
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Building info */}
          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-semibold mb-3">Building Information</h3>
              <StatRow label="Address" value={building.address || '—'} />
              <StatRow label="Units" value={building.total_units || 0} />
              <StatRow label="Status" value={<Badge variant="outline" className="capitalize">{building.status}</Badge>} />
              <StatRow label="Trial" value={building.is_trial ? 'Yes' : 'No'} />
              <StatRow label="Subscription" value={building.subscription_status || '—'} />
              <StatRow label="Primary Admin" value={building.primary_admin_email || '—'} />
              <StatRow label="Created" value={(building.created_at || '').slice(0, 10) || '—'} />
            </CardContent>
          </Card>

          {/* Health card */}
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5" /> Health Score
              </h3>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-2xl ${GRADE_COLOUR[health.grade]}`} data-testid="health-score">
                {health.score}/100
              </div>
              <div className="mt-4 space-y-1">
                <StatRow label="Active users (30d)" value={health.inputs.active_user_pct} suffix="%" />
                <StatRow label="Tickets resolved" value={health.inputs.tickets_resolved_pct} suffix="%" />
                <StatRow label="Notice read-rate" value={health.inputs.notice_read_pct} suffix="%" />
                <StatRow label="Voting participation" value={health.inputs.voting_participation_pct} suffix="%" />
                <StatRow label="Levy paid" value={health.inputs.levy_paid_pct} suffix="%" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Communications */}
        <Card className="mb-6" data-testid="recent-comms-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Recent Communications
                <Badge variant="outline" className="ml-1">{recentComms.length}</Badge>
              </h3>
              {user?.role === 'platform_owner' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/platform/communications?hoas=${hoaId}`)}
                  data-testid="send-new-comm"
                >
                  <Send className="w-4 h-4 mr-1" /> Send New
                </Button>
              )}
            </div>
            {recentComms.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No mass communications have been sent to this building yet.
              </p>
            ) : (
              <div className="divide-y">
                {recentComms.map((c) => {
                  const Icon = c.category === 'emergency' ? AlertTriangle
                    : c.category === 'payment_reminder' ? Mail
                    : c.category === 'notice' ? Bell
                    : MessageSquare;
                  return (
                    <div key={c.communication_id} className="py-3 flex items-start gap-3" data-testid={`comm-${c.communication_id}`}>
                      <div className={`p-2 rounded-full ${c.category === 'emergency' ? 'bg-red-100 text-red-700' : 'bg-indigo-50 text-indigo-700'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{c.subject}</span>
                          <Badge variant="outline" className="capitalize text-xs">{c.category}</Badge>
                          {(c.target_roles || []).length > 0 && (
                            <span className="text-xs text-gray-500">
                              → {c.target_roles.join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {new Date(c.created_at).toLocaleString()} ·
                          Sent by {c.actor_email} ·
                          <span className="ml-1">{c.recipient_count} recipient(s)</span> ·
                          <span className="ml-1">{(c.channels || []).join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Members tabs */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" /> Members
            </h3>
            <Tabs defaultValue="trustees">
              <TabsList>
                <TabsTrigger value="trustees" data-testid="tab-trustees">Trustees ({counts.trustees})</TabsTrigger>
                <TabsTrigger value="residents" data-testid="tab-residents">Residents ({counts.residents})</TabsTrigger>
                <TabsTrigger value="agents" data-testid="tab-agents">Managing Agents ({counts.managing_agents})</TabsTrigger>
                <TabsTrigger value="other">Other</TabsTrigger>
              </TabsList>
              <TabsContent value="trustees" className="mt-3">{renderUserTable(user_groups.trustees)}</TabsContent>
              <TabsContent value="residents" className="mt-3">{renderUserTable(user_groups.residents)}</TabsContent>
              <TabsContent value="agents" className="mt-3">{renderUserTable(user_groups.managing_agents)}</TabsContent>
              <TabsContent value="other" className="mt-3">{renderUserTable([
                ...user_groups.contractors, ...user_groups.insurance, ...user_groups.other,
              ])}</TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
